import React, { useEffect, useRef } from 'react'

import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { Page } from '@templates'
import { ApplicationState } from '@store/types'
import ISafe from '@interco/icons/core/privacy-security/ic_i_safe'
import PrepaymentOfReceivables from '@interco/icons/core/action-navigation/ic_calendar_money'
import { StepRefin, TipoEmprestimo } from '@utils/enums'
import { Radio } from '@interco/inter-ui/components/Radio'
import { Tag } from '@interco/inter-ui/components/Tag'
import TagFunction from '@utils/Tag'
import {
  selectSimulacao,
  setParcelaSelecionada,
  setTotalParcelas,
  trackingRefin,
} from '@store/refinanciamento/simulacao/actions'
import { getParcelaAtualSimulacoes } from '@utils/functions'
import { Button } from '@interco/inter-ui/components/Button'
import { ColorWrapper, TextParagraph, Box } from '@atoms'
import { ListaDescricao } from '@molecules'
import { Colors } from '@interco/cp-react-ui-lib'
import { BridgeService } from '@services/bridge'
import routes from '@routes/routes'
import { useTrackingRefin } from '@hooks'

const OfertaSeguro = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  useTrackingRefin(StepRefin.OFERTA_SEGURO)
  const {
    simulacaoSelecionada = process.env.REACT_APP_SEGURO_DEFAULT_CHECKED === 'true'
      ? TipoEmprestimo.CREDITO_PROTEGIDO
      : TipoEmprestimo.SEM_CREDITO_PROTEGIDO,
    simulations,
    simulationsWithInsurance,
  } = useSelector((state: ApplicationState) => state.refinanciamento.simulacao)
  const { convenioName } = useSelector((state: ApplicationState) => state.refinanciamento.contratos)
  const { loading } = useSelector((state: ApplicationState) => state.refinanciamento.valida)
  const { isApp } = useSelector((state: ApplicationState) => state.ui.navigation)
  const hasInitialized = useRef(false)

  useEffect(() => {
    BridgeService.tac({
      name: 'oferta_seguro',
      actions: ['continuar_refinanciamento', 'autorizar_termo_inss'],
    })
  }, [])

  useEffect(() => {
    if (!hasInitialized.current) {
      dispatch(
        selectSimulacao(
          process.env.REACT_APP_SEGURO_DEFAULT_CHECKED === 'true'
            ? TipoEmprestimo.CREDITO_PROTEGIDO
            : TipoEmprestimo.SEM_CREDITO_PROTEGIDO,
        ),
      )
      hasInitialized.current = true
    }
  }, [dispatch])

  const selecionarParcelasPorSimulacao = () => {
    dispatch(trackingRefin(StepRefin.OFERTA_SEGURO))

    if (
      simulacaoSelecionada === TipoEmprestimo.CREDITO_PROTEGIDO &&
      simulationsWithInsurance.length > 0
    ) {
      const getParcelaAtual = getParcelaAtualSimulacoes(simulationsWithInsurance)
      dispatch(setParcelaSelecionada(getParcelaAtual))
      dispatch(setTotalParcelas(getParcelaAtual.numberOfInstallments))
      dispatch(trackingRefin(StepRefin.CONTRATAR_SIMULACAO_REFIN))
      navigate(routes.RESULTADO_SIMULACAO)
    } else if (
      simulacaoSelecionada === TipoEmprestimo.SEM_CREDITO_PROTEGIDO &&
      simulations.length > 0
    ) {
      const getParcelaAtual = getParcelaAtualSimulacoes(simulations)
      dispatch(setParcelaSelecionada(getParcelaAtual))
      dispatch(setTotalParcelas(getParcelaAtual.numberOfInstallments))
      dispatch(trackingRefin(StepRefin.CONTRATAR_SIMULACAO_REFIN))
      navigate(routes.RESULTADO_SIMULACAO)
    } else {
      navigate(routes.DESQUALIFICACAO, {
        state: {
          convenioNameLocation: convenioName,
          step: 'oferta-seguro',
        },
      })
    }
  }

  return (
    <Page
      footerTwoButtonsSpace="200px"
      stickyFooter={
        <Button
          variant="primary"
          isLoading={loading}
          disabled={loading}
          onClick={() => {
            dispatch(trackingRefin(StepRefin.CLICK_OFERTA_SEGURO_SIMULAR))
            if (isApp) {
              TagFunction.enviarTagueamentoApp({
                screen: 'clt:refin:insurance',
                ref_type: '',
                content_action: 'Dado carregado',
                action_id: '',
                section: 'insurance',
                sub_section: '',
                step: '',
                scenario: 'Aceite seguro',
                ref_figma: '2',
              })
            } else {
              TagFunction.enviarTagueamentoWeb({
                element_action: 'data_loaded',
                element_name: '',
                c_page: window.location.href,
                redirect_url: '',
                section_name: 'payrollloan:refin:insurance',
                section: 'insurance',
                sub_section: '',
                step: '',
                scenario: 'Aceite seguro',
              })
            }
            BridgeService.tic({
              name: 'btn_simular_condicao',
              action: 'simular_condicao_oferta_seguro',
            })
            selecionarParcelasPorSimulacao()
          }}
        >
          Simular melhor condição
        </Button>
      }
    >
      <ColorWrapper color={Colors.GRAY500} fontWeight="600" margin="0 0 24px 0">
        <TextParagraph variant="headline-h2">
          Mantenha o seu refinanciamento protegido
        </TextParagraph>
      </ColorWrapper>

      <Box
        key="simulacao-seguro"
        onClick={() => dispatch(selectSimulacao(TipoEmprestimo.CREDITO_PROTEGIDO))}
        actived={simulacaoSelecionada === TipoEmprestimo.CREDITO_PROTEGIDO}
        justify="space-between"
        padding="16px"
      >
        <div>
          <Tag theme="green" size="small" intensity="dark">
            Mais segurança pra você
          </Tag>
          <ColorWrapper color={Colors.GRAY500} fontWeight="700" margin="8px 0 0 0">
            <TextParagraph variant="body-3">Refinanciamento protegido</TextParagraph>
          </ColorWrapper>

          <ListaDescricao
            iconLeft={<ISafe width={24} height={24} color={Colors.GRAY500} />}
            style={{ marginBottom: '8px', marginTop: '16px' }}
          >
            <ColorWrapper color={Colors.GRAY500} margin="0">
              <TextParagraph variant="caption-1">
                Quitação em caso de morte ou invalidez
              </TextParagraph>
            </ColorWrapper>
          </ListaDescricao>
          <ListaDescricao
            iconLeft={<PrepaymentOfReceivables width={24} height={24} color={Colors.GRAY500} />}
          >
            <ColorWrapper color={Colors.GRAY500} margin="0">
              <TextParagraph variant="caption-1">Crédito liberado em até 48 horas</TextParagraph>
            </ColorWrapper>
          </ListaDescricao>
        </div>
        <Radio
          id="input-radio-seguro"
          onChange={() => dispatch(selectSimulacao(TipoEmprestimo.CREDITO_PROTEGIDO))}
          checked={simulacaoSelecionada === TipoEmprestimo.CREDITO_PROTEGIDO}
          value=""
        />
      </Box>

      <Box
        key="simulacao"
        onClick={() => dispatch(selectSimulacao(TipoEmprestimo.SEM_CREDITO_PROTEGIDO))}
        actived={simulacaoSelecionada === TipoEmprestimo.SEM_CREDITO_PROTEGIDO}
        margin="24px 0 0 0"
        padding="16px"
        justify="space-between"
        align="flex-end"
      >
        <ColorWrapper color={Colors.GRAY500} fontWeight="700">
          <TextParagraph variant="body-3">Continuar sem proteção</TextParagraph>
        </ColorWrapper>

        <Radio
          id="input-radio"
          onChange={() => dispatch(selectSimulacao(TipoEmprestimo.SEM_CREDITO_PROTEGIDO))}
          checked={simulacaoSelecionada === TipoEmprestimo.SEM_CREDITO_PROTEGIDO}
          value=""
        />
      </Box>
    </Page>
  )
}
export default OfertaSeguro
